import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Select,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Box,
  HStack,
  Badge,
} from '@chakra-ui/react';
import { FiAlertTriangle } from 'react-icons/fi';

// Lost reason categories
const LOST_REASONS = [
  { value: '', label: 'Select a reason...' },
  { value: 'price', label: 'Price/Rate Not Competitive' },
  { value: 'timing', label: 'Timing Not Right' },
//  { value: 'competitor', label: 'Went with Competitor' },
  { value: 'no_response', label: 'No Response/Unresponsive' },
  { value: 'unqualified', label: 'Does Not Qualify' },
  { value: 'changed_mind', label: 'Changed Mind/No Longer Interested' },
  { value: 'other', label: 'Other' },
];

/**
 * LostDealReasonModal
 * 
 * Modal that prompts DTC Loan Officers to provide a reason when marking a deal as lost.
 * Required for the Lost Deal Workflow feature.
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {object} deal - The deal being marked as lost
 * @param {object} contact - The associated contact
 * @param {function} onConfirm - Callback with { reason, reasonLabel, details } when confirmed
 * @param {boolean} isLoading - Loading state during submission
 * @param {number} dealCount - For bulk operations, the number of deals being updated
 */
export default function LostDealReasonModal({
  isOpen,
  onClose,
  deal,
  contact,
  onConfirm,
  isLoading = false,
  dealCount = 1,
}) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [errors, setErrors] = useState({});

  const isBulkOperation = dealCount > 1;

  const validate = () => {
    const newErrors = {};

    if (!reason) {
      newErrors.reason = 'Please select a reason';
    }

    if (!details || details.trim().length < 10) {
      newErrors.details = 'Please provide at least 10 characters of detail';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const reasonLabel = LOST_REASONS.find(r => r.value === reason)?.label || reason;
    
    onConfirm({
      reason,
      reasonLabel,
      details: details.trim(),
    });
  };

  const handleClose = () => {
    // Reset form state
    setReason('');
    setDetails('');
    setErrors({});
    onClose();
  };

  // Get display name for the deal
  const dealName = deal?.dealName || 
    (contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : '') ||
    'This Deal';

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={isLoading ? undefined : handleClose}
      size="lg"
      isCentered
      closeOnOverlayClick={!isLoading}
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <FiAlertTriangle color="orange" />
            <Text>Mark Deal as Lost</Text>
          </HStack>
        </ModalHeader>
        {!isLoading && <ModalCloseButton />}
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Info Alert */}
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontSize="sm" fontWeight="semibold">
                  {isBulkOperation 
                    ? `You are marking ${dealCount} deals as Lost`
                    : `You are marking "${dealName}" as Lost`
                  }
                </Text>
                <Text fontSize="xs" color="gray.600">
                  Please provide a reason. This information will be sent to management for review.
                </Text>
              </VStack>
            </Alert>

            {/* Deal Info (single deal only) */}
            {!isBulkOperation && deal && (
              <Box p={3} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
                <VStack align="flex-start" spacing={1}>
                  <HStack>
                    <Text fontSize="sm" fontWeight="bold">{dealName}</Text>
                    {deal.amount > 0 && (
                      <Badge colorScheme="green">${deal.amount?.toLocaleString()}</Badge>
                    )}
                  </HStack>
                  {contact?.email && (
                    <Text fontSize="xs" color="gray.600">{contact.email}</Text>
                  )}
                  {contact?.phone && (
                    <Text fontSize="xs" color="gray.600">{contact.phone}</Text>
                  )}
                  {deal.product && (
                    <Text fontSize="xs" color="gray.500">Product: {deal.product}</Text>
                  )}
                </VStack>
              </Box>
            )}

            {/* Reason Dropdown */}
            <FormControl isRequired isInvalid={!!errors.reason}>
              <FormLabel fontSize="sm">Reason for Loss</FormLabel>
              <Select
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  if (errors.reason) {
                    setErrors(prev => ({ ...prev, reason: undefined }));
                  }
                }}
                placeholder="Select a reason..."
                isDisabled={isLoading}
              >
                {LOST_REASONS.filter(r => r.value).map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
              {errors.reason && (
                <FormErrorMessage>{errors.reason}</FormErrorMessage>
              )}
            </FormControl>

            {/* Details Textarea */}
            <FormControl isRequired isInvalid={!!errors.details}>
              <FormLabel fontSize="sm">Additional Details</FormLabel>
              <Textarea
                value={details}
                onChange={(e) => {
                  setDetails(e.target.value);
                  if (errors.details && e.target.value.trim().length >= 10) {
                    setErrors(prev => ({ ...prev, details: undefined }));
                  }
                }}
                placeholder="Please provide additional context about why this deal was lost..."
                rows={4}
                isDisabled={isLoading}
              />
              <HStack justify="space-between" mt={1}>
                {errors.details ? (
                  <FormErrorMessage>{errors.details}</FormErrorMessage>
                ) : (
                  <Text fontSize="xs" color="gray.500">
                    Minimum 10 characters required
                  </Text>
                )}
                <Text fontSize="xs" color={details.length >= 10 ? 'green.500' : 'gray.400'}>
                  {details.length} characters
                </Text>
              </HStack>
            </FormControl>

            {/* Notice about email */}
            <Alert status="info" size="sm" borderRadius="md">
              <AlertIcon />
              <Text fontSize="xs">
                An email notification will be sent to LostDeal@future.loans with these details.
              </Text>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button 
            variant="ghost" 
            mr={3} 
            onClick={handleClose}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Submitting..."
          >
            {isBulkOperation 
              ? `Mark ${dealCount} Deals as Lost`
              : 'Mark as Lost'
            }
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// Export the lost reasons for use in other components
export { LOST_REASONS };
